"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { ChevronRight } from "lucide-react"
import { useForm } from "react-hook-form"
import { safeInternalRedirect } from "@/lib/auth/safe-redirect"
import { isNativeApp } from "@/lib/native"
import { createClient } from "@/lib/supabase/client"
import { markLoginSplash } from "@/components/login-splash"
import { GoogleAuthButton } from "@/components/auth/google-auth-button"
import { AppleAuthButton } from "@/components/auth/apple-auth-button"
import { AuthPageShell, authFieldClass, authPillGradient } from "@/components/auth/auth-page-shell"
import { Lozenge } from "@/components/ui/lozenge"

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const EyeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
)

type LoginFormValues = {
  identifier: string
  password: string
  rememberMe: boolean
}

export default function LoginPage() {
  const t = useTranslations("pages.auth.login")
  const searchParams = useSearchParams()
  const inviteToken = searchParams.get("invite")
  const message = searchParams.get("message")
  const rawRedirect = searchParams.get("redirect")
  const redirectTo = safeInternalRedirect(rawRedirect, "/dashboard/barber")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  // Inside the shell this route IS the app's launch screen, so a signed-in
  // person saw the sign-in form for the length of a session check before being
  // sent to their dashboard — the app looked like it was logging them out and
  // back in on every open. Hold the dark screen until the answer is known.
  //
  // Starts false in a browser: a bookmarked /auth/login for someone signed out
  // should show the form immediately rather than wait on a network round-trip.
  // Set in an effect, not in useState's initializer, because isNativeApp()
  // reads the Capacitor bridge and the server has no idea.
  const [checkingSession, setCheckingSession] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: {
      // Default ON: keep users signed in across browser restarts unless they
      // deliberately opt out. Prevents the "logged out every time I close the
      // browser" complaint for anyone who never touches this checkbox.
      rememberMe: true,
    },
  })

  // localStorage doesn't exist during SSR — reading it in defaultValues
  // crashes the server render of this page. Restore the saved choice on mount.
  useEffect(() => {
    // Only an explicit prior opt-out flips this off; anything else stays ON.
    if (localStorage.getItem("booth_remember_me") === "false") {
      setValue("rememberMe", false)
    }
  }, [setValue])

  // Already signed in? Don't show a login form.
  //
  // On the web this is a small courtesy — a bookmarked /auth/login should take
  // you where you were going. In the iOS shell it is the whole entry policy:
  // the app opens on this route (capacitor.config.ts), so signed out lands on
  // the form and signed in lands in the product, without the app needing to
  // know anything about auth itself.
  //
  // A hard assignment rather than router.replace: the destination reads the
  // session from cookies on the server, and a client-side navigation can beat
  // them into place.
  useEffect(() => {
    let cancelled = false
    const native = isNativeApp()
    if (native) setCheckingSession(true)
    void (async () => {
      try {
        const {
          data: { user },
        } = await createClient().auth.getUser()
        if (!cancelled && user) {
          // Leave the gate up: the document is on its way out, and lowering it
          // would flash the form between the answer and the navigation.
          window.location.replace(redirectTo)
          return
        }
      } catch {
        // No session, or offline. Either way the form is the right answer.
      }
      if (!cancelled) setCheckingSession(false)
    })()
    return () => {
      cancelled = true
    }
  }, [redirectTo])

  async function onSubmit(data: LoginFormValues) {
    setLoading(true)
    setErrorMessage("")

    try {
      // Mirror the choice into localStorage as well: the server route uses the
      // posted flag for the auth-cookie maxAge, and the browser client reads
      // this key (shouldPersistSession) for cookies IT writes on later token
      // rotations. Both need to agree.
      localStorage.setItem("booth_remember_me", String(data.rememberMe))

      // Sign-in runs server-side (app/api/auth/login) so every attempt passes
      // through the rate limiter, and so the role lookup rides along on the
      // same round trip instead of costing a second one from the browser.
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: data.identifier,
          password: data.password,
          rememberMe: data.rememberMe,
        }),
      })
      const result = await res.json()

      if (!res.ok) {
        setErrorMessage(result.error || t("genericError"))
        setLoading(false)
        return
      }

      // Signed in — arm the one-shot splash the dashboard plays on arrival.
      markLoginSplash()

      const userRole = result.role as "barber" | "owner" | null
      if (userRole) localStorage.setItem("user-role", userRole)

      // A hard navigation, not router.push: the session now lives in cookies
      // set by the server, and only a fresh document load re-initializes the
      // browser Supabase client and the role provider from them. This is the
      // same handoff the Google OAuth callback already performs. The login
      // splash covers the load.
      // Deliberately leave `loading` true: the document is on its way out, and
      // clearing it here would flash the button back to its idle label mid-nav.
      window.location.assign(destinationFor(userRole))
    } catch (error) {
      console.error("Login error:", error)
      setErrorMessage(t("genericError"))
      setLoading(false)
    }
  }

  // Invite acceptance wins, then an explicit ?redirect= that survived
  // sanitization, then the role's home. No pro role means a client account
  // (walled off from the pro role system) — they get the client home rather
  // than the barber default.
  function destinationFor(userRole: "barber" | "owner" | null): string {
    if (inviteToken) return `/invite/${inviteToken}/accept`
    if (rawRedirect && safeInternalRedirect(rawRedirect, "") !== "") return redirectTo
    if (userRole === "owner") return "/dashboard/owner"
    if (userRole === "barber") return "/dashboard/barber"
    return "/dashboard/client"
  }

  // Not `fixed inset-0`: a full-screen fixed overlay is what makes iOS size the
  // page against the wrong viewport (see the bottom-bar recipe). A plain
  // full-height block in flow does the same job and nothing else.
  if (checkingSession) {
    return <div className="min-h-screen bg-[#0C0C0C]" aria-hidden />
  }

  return (
    <AuthPageShell>
        <div className="space-y-5">
          <div className="space-y-4 text-center">
            <div className="flex items-center justify-center">
              {/* On the web the mark is the way back to the site. In the app
                  there is no site to go back to: someone who installed BOOTH
                  is here to sign in, and the marketing page inside the shell
                  is the App Store's "repackaged website" reading. The press
                  is stopped rather than the markup changed, so the server and
                  the client still render the same thing. */}
              <Link
                href="/"
                onClick={(e) => {
                  if (isNativeApp()) e.preventDefault()
                }}
                className="flex items-center justify-center"
              >
                <Image src="/images/booth-logo-white.png" alt="BOOTH" width={87} height={22} priority />
              </Link>
            </div>

            <div className="space-y-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-medium text-neutral-300">
                <Lozenge className="w-2.5" />
                {inviteToken ? t("subtitleInvite") : t("eyebrow")}
              </span>
              <h1 className="text-xl font-semibold tracking-tight text-white">{t("heading")}</h1>
            </div>
          </div>

          {errorMessage && (
            <div className="rounded-2xl border border-red-500/15 bg-red-500/[0.06] p-3 text-[11px] text-red-400">
              {errorMessage}
            </div>
          )}

          {message && (
            <div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/[0.06] p-3 text-[11px] text-emerald-400">
              {message}
            </div>
          )}

          {/* Apple above Google: guideline 4.8 wants it offered as an equal,
              and Apple's HIG asks that it be no less prominent than the other
              options. Same shape and size, first in the stack. */}
          <AppleAuthButton
            label={t("appleCta")}
            errorFallback={t("appleError")}
            inviteToken={inviteToken}
            onError={setErrorMessage}
          />

          <GoogleAuthButton
            label={t("googleCta")}
            errorFallback={t("googleError")}
            inviteToken={inviteToken}
            onError={setErrorMessage}
          />

          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] tracking-[0.3em] text-neutral-600">{t("or")}</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="identifier" className="sr-only">
                {t("identifierLabel")}
              </label>
              <input
                id="identifier"
                type="text"
                placeholder={t("identifierLabel")}
                className={authFieldClass}
                {...register("identifier", {
                  required: t("identifierRequired"),
                })}
              />
              {errors.identifier && <p className="text-[11px] text-red-400">{errors.identifier.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="sr-only">
                {t("passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("passwordLabel")}
                  className={`${authFieldClass} pr-12`}
                  {...register("password", {
                    required: t("passwordRequired"),
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-white"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password && <p className="text-[11px] text-red-400">{errors.password.message}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  className="h-3.5 w-3.5 cursor-pointer rounded border-neutral-600 bg-[#0a0a0a] text-[#0F8843] transition-all focus:ring-2 focus:ring-[#0F8843]/20"
                  {...register("rememberMe")}
                />
                <label htmlFor="rememberMe" className="cursor-pointer select-none text-[11px] text-neutral-400">
                  {t("rememberMe")}
                </label>
              </div>
              <Link href="/auth/forgot-password" className="text-[11px] text-neutral-400 transition-colors hover:text-white">
                {t("forgotPassword")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={authPillGradient}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-white/10 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {loading ? t("submitting") : t("submit")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </form>

          <p className="text-center text-[11px] text-neutral-400">
            {t("noAccount")}{" "}
            <Link
              href={inviteToken ? `/auth/signup?invite=${inviteToken}` : "/auth/signup"}
              className="font-semibold text-white hover:underline"
            >
              {t("createAccount")}
            </Link>
          </p>

          {/* The way out of the login wall.

              App Store guideline 5.1.1(i): an app may not require an account to
              reach what isn't account-based, and the shops directory isn't —
              it's public on the web. Opening the app on /auth/login is still
              right (see capacitor.config.ts), but with no exit it reads as a
              wall, and a reviewer who can't get past one rejects the build.
              Search, not the marketing page: someone who installed BOOTH has
              already been sold. */}
          <p className="text-center text-[11px] text-neutral-500">
            <Link href="/search" className="text-neutral-300 transition-colors hover:text-white">
              {t("browseAsGuest")}
            </Link>
          </p>

          <p className="text-center text-[11px] text-neutral-500">
            {t("footerPrefix")}{" "}
            <Link href="/terms" className="text-neutral-300 hover:text-white">
              {t("terms")}
            </Link>{" "}
            {t("and")}{" "}
            <Link href="/privacy" className="text-neutral-300 hover:text-white">
              {t("privacy")}
            </Link>
          </p>
        </div>
    </AuthPageShell>
  )
}
